import { SidebarSimpleIcon } from "@phosphor-icons/react";
import { Button, Sheet, SheetClose, SheetContent, SheetTrigger } from "@stackmorph/ui";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link, Outlet } from "react-router";

import { Icon } from "@/client/components/icon";
import { OnboardingModal } from "@/client/components/onboarding/onboarding-modal";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useUser } from "@/client/services/user/user";

import { Sidebar } from "./_components/sidebar";

export const DashboardLayout = () => {
  const { user } = useUser();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Enhanced Background with Multiple Gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-primary/5 to-background" />
      
      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -left-1/4 -top-1/4 h-1/2 w-1/2 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5,
          }}
          className="absolute -right-1/4 -bottom-1/4 h-1/2 w-1/2 rounded-full bg-gradient-to-tl from-blue-500/20 to-cyan-500/20 blur-3xl"
        />
      </div>

      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.03]" />

      {/* Content */}
      <div className="relative z-10">
        {/* Mobile Sidebar Sheet */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="fixed left-4 top-4 z-50 lg:hidden">
            <Button size="icon" variant="outline" className="rounded-full bg-background/80 shadow-2xl backdrop-blur-md border-white/20">
              <SidebarSimpleIcon />
            </Button>
          </SheetTrigger>

          <SheetContent showClose={false} side="left" className="border-r-border/50 bg-background/95 backdrop-blur-xl p-0 w-80">
            <SheetClose asChild className="absolute right-4 top-4">
              <Button size="icon" variant="ghost" className="rounded-full">
                <SidebarSimpleIcon />
              </Button>
            </SheetClose>

            <div className="p-6">
              <Sidebar setOpen={setOpen} />
            </div>
          </SheetContent>
        </Sheet>

        {/* Desktop Layout - Top Bar + Content */}
        <div className="container mx-auto flex min-h-screen flex-col lg:px-8 px-4">
          {/* Enhanced Top Navigation Bar for Desktop */}
          <div className="hidden lg:flex lg:items-center lg:justify-between lg:py-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-8"
            >
              <Link to="/" className="flex items-center space-x-3 group">
                <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.5 }}>
                  <Icon size={36} />
                </motion.div>
                <span className="text-2xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                  StackMorph
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <UserOptions>
                <Button variant="ghost" size="sm" className="rounded-full hover:bg-white/10 backdrop-blur-sm">
                  <UserAvatar size={32} className="mr-2" />
                  <span className="max-w-32 truncate">{user?.name}</span>
                </Button>
              </UserOptions>
            </motion.div>
          </div>

          {/* Main Content Area with Enhanced Animation */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative flex-1 pb-24 lg:pb-12"
          >
            <Outlet />
          </motion.div>
        </div>

        {/* Enhanced Bottom Navigation */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-background/80 shadow-2xl backdrop-blur-2xl lg:bottom-6 lg:left-1/2 lg:right-auto lg:w-auto lg:-translate-x-1/2 lg:rounded-2xl lg:border lg:border-white/20"
        >
          <div className="container mx-auto lg:px-0 px-4">
            <div className="flex items-center justify-around py-4 lg:space-x-3 lg:px-8">
              <Sidebar />
            </div>
          </div>
        </motion.div>

        <OnboardingModal />
      </div>
    </div>
  );
};
